/**
 * GreenSyn Inventory — the human code scheme.
 *
 * Every human key in the system — item, location, supplier, recipe, lot,
 * batch, recovery — is built from these definitions, so the generator, the
 * display and any future reader cannot disagree about what a code means.
 *
 * Two rules run through the whole scheme:
 *
 *  - **Encode only creation-time facts.** A segment may say what something
 *    was when it was created (its kind, its mnemonic, the date, the origin),
 *    never something that can later change. `Item.kind` is editable and names
 *    can be edited; a code never is, so an `RM-` prefix records that the item
 *    *was created as* a raw material rather than asserting what it is today.
 *  - **Suggest, never impose.** These functions format a candidate. A person
 *    may always type their own code (a supplier's own batch number is the
 *    obvious case), and the database's unique constraints are the backstop.
 */
import {
  ITEM_KINDS,
  LOCATION_TYPES,
  type ItemKind,
  type LocationType,
  type LotOrigin,
} from './enums.js';

/** The kinds of record that carry a generated code. */
export type CodeEntity =
  | 'item'
  | 'location'
  | 'supplier'
  | 'recipe'
  | 'lot'
  | 'batch'
  | 'recovery';

/** One decoded piece of a code, for display beside the generated value. */
export interface CodeSegment {
  /** The literal text, e.g. `RM`, `SHP`, `20261008`. */
  value: string;
  /** What that text says, e.g. `raw material`, `mnemonic`, `date`. */
  label: string;
}

/**
 * A server-generated code suggestion.
 *
 * The code is what gets stored if the person accepts the suggestion; the
 * segments are the same code decoded for the person reading the field. The API
 * always computes the suggestion so two clients cannot invent different codes
 * from the same record.
 */
export interface SuggestedCode {
  code: string;
  segments: CodeSegment[];
}

/** The leading segment of an item code, from its kind. */
export const ITEM_CODE_PREFIXES: Record<ItemKind, string> = {
  raw_material: 'RM',
  packaging: 'PK',
  intermediate: 'INT',
  finished_good: 'FG',
  other: 'OTH',
};

/** The leading segment of a location code, from its type. */
export const LOCATION_CODE_PREFIXES: Record<LocationType, string> = {
  warehouse: 'WH',
  production: 'LINE',
  quarantine: 'QC',
  scrap: 'SCRAP',
  lab: 'LAB',
};

/**
 * The lot origin segment. `PUR` (purchased) is how every receipt is tagged;
 * `RCV` (recovered) is the tag that already existed and must stay, because a
 * recovered lot's label has to announce itself (docs/PLAN.md section 23).
 */
export const LOT_ORIGIN_TAGS: Record<LotOrigin, string> = {
  purchased: 'PUR',
  produced: 'PRD',
  recovered: 'RCV',
  adjusted: 'ADJ',
};

const ITEM_KIND_LABELS: Record<ItemKind, string> = {
  raw_material: 'raw material',
  packaging: 'packaging',
  intermediate: 'intermediate',
  finished_good: 'finished good',
  other: 'other',
};

const LOCATION_TYPE_LABELS: Record<LocationType, string> = {
  warehouse: 'warehouse',
  production: 'production line',
  quarantine: 'quarantine',
  scrap: 'scrap',
  lab: 'lab / R&D',
};

/**
 * The letter a location contributes to a batch's site segment. Taken from the
 * type rather than the code's first character, so two types that share an
 * initial — `LINE` and `LAB` — still produce different sites.
 */
const LOCATION_SITE_LETTERS: Record<LocationType, string> = {
  warehouse: 'W',
  production: 'L',
  quarantine: 'Q',
  scrap: 'S',
  lab: 'R',
};

const LOT_ORIGIN_LABELS: Record<LotOrigin, string> = {
  purchased: 'received',
  produced: 'produced',
  recovered: 'recovered',
  adjusted: 'adjusted',
};

/**
 * Words that describe what a thing *is* rather than which one it is: dropping
 * them turns "GreenSyn API — Crystalline" into `GAC` instead of `GACX`, and
 * "Sodium Hydroxide Pellets" into `SHP`.
 */
const MNEMONIC_STOP_WORDS = new Set([
  'and',
  'the',
  'for',
  'with',
  'of',
  'grade',
  'grades',
  'type',
  'kind',
  'class',
]);

const MNEMONIC_MAX_LENGTH = 4;

/**
 * A short, readable mnemonic from a name.
 *
 * Several words become their initials (`SHP`); one word becomes its first
 * letters (`ACN` for acetonitrile). Numbers are dropped, so a chemical's
 * locants never end up in the code. The result is only ever a *suggestion* —
 * the person may type any mnemonic they like into the code field.
 */
export function mnemonicFromName(name: string, maxLength = 3): string {
  const words = name
    .normalize('NFKD')
    // Strip the combining marks NFKD separated out, so `é` becomes `E`.
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .filter((word) => word.length > 0 && !/^\d+$/.test(word))
    .filter((word) => !MNEMONIC_STOP_WORDS.has(word.toLowerCase()));

  if (words.length === 0) return 'X';

  const letters =
    words.length === 1 ? words[0]!.slice(0, 3) : words.map((word) => word[0]!).join('');

  return letters.slice(0, Math.min(Math.max(1, maxLength), MNEMONIC_MAX_LENGTH));
}

/**
 * A location's label is the part of its name that is not merely its type:
 * "Warehouse A" gives `A`, "Production Line 1" gives `1`, and a name that is
 * all type words ("Quarantine Hold") gives nothing, so the caller falls back
 * to a sequence number.
 */
export function locationLabelFromName(name: string): string | null {
  const words = name
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .filter((word) => word.length > 0);

  const typeWords = new Set([
    'WAREHOUSE',
    'STORE',
    'PRODUCTION',
    'LINE',
    'QUARANTINE',
    'HOLD',
    'SCRAP',
    'WASTE',
    'AREA',
    'ROOM',
    'STAGE',
  ]);

  const remaining = words.filter((word) => !typeWords.has(word));
  const last = remaining.at(-1);
  if (!last) return null;

  return /^\d+$/.test(last) ? last : last.slice(0, 3);
}

/** A padded sequence number. Grows past its width rather than truncating. */
export function codeSequence(value: number, width: number): string {
  if (!Number.isInteger(value) || value < 1) {
    throw new Error(`A code sequence starts at 1, received ${value}`);
  }
  return String(value).padStart(width, '0');
}

/* -------------------------------------------------------------------------- */
/* The compact alphabet                                                        */
/* -------------------------------------------------------------------------- */

/**
 * The VIN-safe alphabet: `0-9` and `A-Z` without `I`, `O` and `Q`, the three
 * letters a person most often misreads as `1` and `0`. Every compact segment of
 * a batch code (its date, site serial) is written in this alphabet.
 */
export const CODE_ALPHABET = '0123456789ABCDEFGHJKLMNPRSTUVWXYZ';

/** A number in the compact alphabet, optionally padded — `1` at width 3 is `001`. */
export function codeBase36(value: number, width = 0): string {
  if (!Number.isInteger(value) || value < 0) {
    throw new Error(`A code value cannot be negative or fractional, received ${value}`);
  }

  let remaining = value;
  let encoded = '';
  do {
    encoded = CODE_ALPHABET.charAt(remaining % CODE_ALPHABET.length) + encoded;
    remaining = Math.floor(remaining / CODE_ALPHABET.length);
  } while (remaining > 0);

  return encoded.padStart(width, CODE_ALPHABET.charAt(0));
}

/** The number a compact-alphabet string stands for. Throws on a foreign character. */
export function codeBaseValue(text: string): number {
  let value = 0;
  for (const char of text.toUpperCase()) {
    const digit = CODE_ALPHABET.indexOf(char);
    if (digit < 0) throw new Error(`'${char}' is not in the code alphabet`);
    value = value * CODE_ALPHABET.length + digit;
  }
  return value;
}

/* -------------------------------------------------------------------------- */
/* ISO 7064 MOD 37,36 check character                                          */
/* -------------------------------------------------------------------------- */

/** The ISO 7064 value space — deliberately includes `I`, `O` and `Q`. */
const CHECK_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const CHECK_MODULUS = CHECK_ALPHABET.length; // 36

/**
 * The hybrid-system recursion from ISO/IEC 7064 MOD 37,36. A protected string
 * (body plus check character) ends in state 1; the algorithm detects every
 * single substitution and every adjacent transposition.
 */
function iso7064State(text: string): number {
  let state = CHECK_MODULUS / 2;

  for (const char of text.toUpperCase()) {
    const value = CHECK_ALPHABET.indexOf(char);
    if (value < 0) throw new Error(`'${char}' is not an ISO 7064 MOD 37,36 character`);
    state =
      ((((state === 0 ? CHECK_MODULUS : state) * 2) % (CHECK_MODULUS + 1)) + value) % CHECK_MODULUS;
  }

  return state;
}

/** The ISO 7064 MOD 37,36 check character for a body. */
export function codeCheckChar(body: string): string {
  const state = iso7064State(body);
  const doubled = (((state === 0 ? CHECK_MODULUS : state) * 2) % (CHECK_MODULUS + 1));
  const value = ((1 - doubled) % CHECK_MODULUS + CHECK_MODULUS) % CHECK_MODULUS;
  return CHECK_ALPHABET.charAt(value);
}

/** Whether a body-plus-check string satisfies its check character. */
export function codeCheckValid(protectedCode: string): boolean {
  return iso7064State(protectedCode) === 1;
}

/** Uppercase, strip anything that cannot appear in a mnemonic, cap the length. */
export function normaliseMnemonic(value: string, maxLength = MNEMONIC_MAX_LENGTH): string {
  const cleaned = value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, Math.min(Math.max(1, maxLength), MNEMONIC_MAX_LENGTH));

  if (cleaned.length === 0) throw new Error('A mnemonic needs at least one letter or digit');
  return cleaned;
}

/* -------------------------------------------------------------------------- */
/* Prefixes                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The series a sequence number is scoped to. Suggestions read existing codes
 * with the same prefix, so the prefix functions and the builders must agree —
 * and they do, because the builders are written in terms of them.
 */
export function itemCodePrefix(kind: ItemKind, mnemonic: string): string {
  return `${ITEM_CODE_PREFIXES[kind]}-${normaliseMnemonic(mnemonic)}-`;
}

export function supplierCodePrefix(mnemonic: string): string {
  return `SUP-${normaliseMnemonic(mnemonic)}-`;
}

export function recipeCodePrefix(mnemonic: string): string {
  return `RCP-${normaliseMnemonic(mnemonic)}-`;
}

/** The `WH-` of a location code; the suffix is a label or a sequence. */
export function locationCodePrefix(type: LocationType): string {
  return `${LOCATION_CODE_PREFIXES[type]}-`;
}

export function lotCodePrefix(itemCodeValue: string, origin: LotOrigin, when: Date): string {
  return `${itemCodeValue}-${LOT_ORIGIN_TAGS[origin]}-${codeDateSegment(when)}-`;
}

/* -------------------------------------------------------------------------- */
/* Batch identifiers                                                           */
/* -------------------------------------------------------------------------- */

/**
 * A batch's product is identified by its kind, its mnemonic and its item
 * sequence — `FG-API-001` becomes `FAPI1` — so the code names the exact item
 * without spelling out the whole item code.
 */
export interface BatchProduct {
  code: string;
  kind: ItemKind;
}

/**
 * The same 40-character budget as every other code. A generated batch code is
 * around twenty characters, so this is only a backstop against a pathological
 * item code.
 */
export const BATCH_CODE_MAX_LENGTH = 40;

/** The `RM-SHP-001`-style parts of an item code, or null for a hand-made code. */
function parseItemCodeParts(
  code: string,
): { mnemonic: string | null; sequence: string | null } | null {
  const [prefix, ...rest] = code.toUpperCase().split('-');
  if (!(Object.values(ITEM_CODE_PREFIXES) as string[]).includes(prefix ?? '')) return null;

  if (rest.length === 1 && /^\d+$/.test(rest[0]!)) {
    return { mnemonic: null, sequence: rest[0]! };
  }
  if (rest.length === 2 && /^[A-Z0-9]+$/.test(rest[0]!) && /^\d+$/.test(rest[1]!)) {
    return { mnemonic: rest[0]!, sequence: rest[1]! };
  }
  return null;
}

/** `FAPI1` — kind initial + mnemonic + item number, leading zeros stripped. */
export function batchProductKey(product: BatchProduct): string {
  const kindInitial = ITEM_CODE_PREFIXES[product.kind].charAt(0);
  const parts = parseItemCodeParts(product.code);

  if (parts) {
    const sequence = parts.sequence === null ? '' : String(Number(parts.sequence));
    return `${kindInitial}${parts.mnemonic ?? ''}${sequence}`;
  }

  const fallback = product.code.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3) || 'X';
  return `${kindInitial}${fallback}`;
}

/**
 * `26A8` — `YY`, then month and day in the compact alphabet. Every
 * date-bearing code uses this one segment, so a batch, a lot and a recovery all
 * say when in the same four characters.
 */
export function codeDateSegment(when: Date): string {
  return [
    String(when.getUTCFullYear() % 100).padStart(2, '0'),
    codeBase36(when.getUTCMonth() + 1),
    codeBase36(when.getUTCDate()),
  ].join('');
}

/**
 * `WA` for `WH-A`, `L1` for `LINE-1`, `R2` for `LAB-2`, `SC` for `SCRAP` — the
 * location type's site letter plus its suffix, so a label says where as well as
 * when.
 */
export function batchSiteSegment(locationCode: string): string {
  const upper = locationCode.toUpperCase().replace(/[^A-Z0-9-]/g, '');
  const [head = '', ...rest] = upper.split('-');
  const suffix = rest.join('');

  const type = (Object.keys(LOCATION_CODE_PREFIXES) as LocationType[]).find(
    (candidate) => LOCATION_CODE_PREFIXES[candidate] === head,
  );
  const letter = type ? LOCATION_SITE_LETTERS[type] : head.charAt(0) || 'X';

  if (suffix) {
    const trimmed = suffix.replace(/^0+(?=\d)/, '');
    return `${letter}${trimmed}`.slice(0, 3);
  }
  return `${letter}${head.slice(1)}`.slice(0, 2).padEnd(2, 'X');
}

/** The undashed body a batch code's check character is computed over. */
function batchBody(
  product: BatchProduct,
  locationCode: string,
  when: Date,
  serial: string,
): string {
  return `B${batchProductKey(product)}${codeDateSegment(when)}${batchSiteSegment(locationCode)}${serial}`;
}

export function batchCodePrefix(product: BatchProduct, locationCode: string, when: Date): string {
  return `B-${batchProductKey(product)}-${codeDateSegment(when)}-${batchSiteSegment(locationCode)}-`;
}

export function recoveryCodePrefix(when: Date): string {
  return `RCV-${codeDateSegment(when)}-`;
}

/* -------------------------------------------------------------------------- */
/* Builders                                                                    */
/* -------------------------------------------------------------------------- */

/** `RM-SHP-001`, `PK-AGB-001`, `FG-API-001`. Sequence is per prefix+mnemonic. */
export function itemCode(kind: ItemKind, mnemonic: string, sequence: number): string {
  return `${itemCodePrefix(kind, mnemonic)}${codeSequence(sequence, 3)}`;
}

/** `SUP-ACME-001`. Sequence is per supplier mnemonic. */
export function supplierCode(mnemonic: string, sequence: number): string {
  return `${supplierCodePrefix(mnemonic)}${codeSequence(sequence, 3)}`;
}

/** `RCP-CRY-001`. Sequence is per recipe mnemonic; the version is a field. */
export function recipeCode(mnemonic: string, sequence: number): string {
  return `${recipeCodePrefix(mnemonic)}${codeSequence(sequence, 3)}`;
}

/**
 * `WH-A`, `LINE-1`, `QC-02`. The suffix is the name's label when it has one,
 * otherwise a two-digit sequence.
 */
export function locationCode(type: LocationType, suffix: string): string {
  const cleaned = suffix.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (cleaned.length === 0) throw new Error('A location code needs a label or a number');
  return `${locationCodePrefix(type)}${cleaned}`;
}

/** `RM-ACN-001-PUR-20261008-01` — a lot that announces how it came to be. */
export function lotCode(
  itemCodeValue: string,
  origin: LotOrigin,
  when: Date,
  sequence: number,
): string {
  return `${lotCodePrefix(itemCodeValue, origin, when)}${codeSequence(sequence, 2)}`;
}

/**
 * `B-FAPI1-26A8-L1-001-K` — a VIN-inspired batch identifier: record type,
 * product, compact date, site, serial and an ISO 7064 check character.
 */
export function batchCode(
  product: BatchProduct,
  locationCode: string,
  when: Date,
  sequence: number,
): string {
  const serial = codeBase36(sequence, 3);
  const code = `${batchCodePrefix(product, locationCode, when)}${serial}-${codeCheckChar(
    batchBody(product, locationCode, when, serial),
  )}`;

  if (code.length > BATCH_CODE_MAX_LENGTH) {
    throw new Error(`Batch code ${code} exceeds ${BATCH_CODE_MAX_LENGTH} characters`);
  }
  return code;
}

/** `RCV-20261008-001` — the recovery record itself, not the lot it creates. */
export function recoveryCode(when: Date, sequence: number): string {
  return `${recoveryCodePrefix(when)}${codeSequence(sequence, 3)}`;
}

/* -------------------------------------------------------------------------- */
/* Decoding                                                                    */
/* -------------------------------------------------------------------------- */

const BATCH_CODE_PATTERN =
  /^B-([A-Z][A-Z0-9]*)-([0-9]{2}[0-9A-Z]{2})-([A-Z0-9]{2,3})-([0-9A-Z]{3,})-([0-9A-Z])$/;

interface ParsedBatchCode {
  product: string;
  date: string;
  site: string;
  serial: string;
  check: string;
  /** The undashed characters the check character protects. */
  body: string;
}

/** The compact `YY`+base36 date, decoded. Null for anything else. */
function decodeCodeDate(segment: string): { year: number; month: number; day: number } | null {
  if (!/^\d{2}[0-9A-Z]{2}$/.test(segment)) return null;

  try {
    const month = codeBaseValue(segment.charAt(2));
    const day = codeBaseValue(segment.charAt(3));
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return { year: 2000 + Number(segment.slice(0, 2)), month, day };
  } catch {
    return null;
  }
}

/** A date segment in either encoding: compact (`26A8`) or legacy (`20261008`). */
function decodeAnyDate(segment: string): { year: number; month: number; day: number } | null {
  const compact = decodeCodeDate(segment);
  if (compact) return compact;

  const legacy = /^(\d{4})(\d{2})(\d{2})$/.exec(segment);
  if (!legacy) return null;
  return { year: Number(legacy[1]), month: Number(legacy[2]), day: Number(legacy[3]) };
}

function isoDate(date: { year: number; month: number; day: number }): string {
  return [
    date.year,
    String(date.month).padStart(2, '0'),
    String(date.day).padStart(2, '0'),
  ].join('-');
}

/** The compact `B-…` batch identifier, or null when this is not one of ours. */
export function parseBatchCode(code: string): ParsedBatchCode | null {
  const match = BATCH_CODE_PATTERN.exec(code.trim().toUpperCase());
  if (!match) return null;

  const [, product, date, site, serial, check] = match as unknown as [
    string,
    string,
    string,
    string,
    string,
    string,
  ];
  if (!decodeCodeDate(date)) return null;

  return { product, date, site, serial, check, body: `B${product}${date}${site}${serial}` };
}

function batchProductLabel(product: string): string {
  const kind = (Object.keys(ITEM_CODE_PREFIXES) as ItemKind[]).find(
    (candidate) => ITEM_CODE_PREFIXES[candidate].charAt(0) === product.charAt(0),
  );
  const rest = product.slice(1);
  const digits = /(\d+)$/.exec(rest)?.[1];
  const mnemonic = digits ? rest.slice(0, -digits.length) : rest;

  const parts = [kind ? ITEM_KIND_LABELS[kind] : 'product'];
  if (mnemonic) parts.push(mnemonic);
  if (digits) parts.push(`item ${Number(digits)}`);
  return parts.join(' · ');
}

/**
 * Whether a code carries a valid ISO 7064 check character. `null` means the
 * code is not one of ours — a legacy code or a hand-written override — which is
 * never an error. Only a scheme-shaped code can fail its check.
 */
export function verifyCodeCheck(code: string): boolean | null {
  const parsed = parseBatchCode(code);
  if (!parsed) return null;

  try {
    return codeCheckValid(parsed.body + parsed.check);
  } catch {
    return null;
  }
}

function masterDataSegments(prefix: string, prefixLabel: string, rest: string[]): CodeSegment[] {
  const segments: CodeSegment[] = [{ value: prefix, label: prefixLabel }];
  if (rest.length === 0) return segments;

  const last = rest.at(-1)!;

  // A legacy-style code has one part after the prefix, and that part is the
  // sequence (`RM-003`). A mnemonic-bearing code has the sequence last
  // (`RM-SHP-001`); everything before it is the mnemonic.
  if (rest.length === 1) {
    segments.push({ value: last, label: /^\d+$/.test(last) ? 'sequence' : 'mnemonic' });
    return segments;
  }

  const trailingIsSequence = /^\d+$/.test(last);
  const labelParts = trailingIsSequence ? rest.slice(0, -1) : rest;
  for (const part of labelParts) segments.push({ value: part, label: 'mnemonic' });
  if (trailingIsSequence) segments.push({ value: last, label: 'sequence' });

  return segments;
}

/**
 * Split a code into its labelled parts for display. Unknown or hand-written
 * codes decode to a single segment rather than guessing — a `TEST-1` is
 * legitimately just a code.
 */
export function describeCode(code: string): CodeSegment[] {
  const value = code.trim();
  if (value.length === 0) return [];

  // B-<product>-<date>-<site>-<serial>-<check>
  const compactBatch = parseBatchCode(value);
  if (compactBatch) {
    const date = decodeCodeDate(compactBatch.date)!;
    return [
      { value: 'B', label: 'batch' },
      { value: compactBatch.product, label: batchProductLabel(compactBatch.product) },
      { value: compactBatch.date, label: `date ${isoDate(date)}` },
      { value: compactBatch.site, label: 'site' },
      { value: compactBatch.serial, label: 'serial' },
      { value: compactBatch.check, label: 'check' },
    ];
  }

  // BATCH-<product>-<YYYYMMDD>-<NNN> — the format before the compact scheme.
  const batch = /^BATCH-(.+)-(\d{8})-(\d{3,})$/.exec(value);
  if (batch) {
    return [
      { value: 'BATCH', label: 'batch' },
      { value: batch[1]!, label: 'product' },
      { value: batch[2]!, label: 'date' },
      { value: batch[3]!, label: 'sequence' },
    ];
  }

  // <item>-<PUR|PRD|RCV|ADJ>-<date>-<NN>; the date is compact, or the legacy
  // eight-digit stamp on rows created before the compact scheme.
  const lot = /^(.+)-(PUR|PRD|RCV|ADJ)-([0-9]{2}[0-9A-Z]{2}|\d{8})-(\d{2,})$/.exec(value);
  if (lot) {
    const origin = (Object.keys(LOT_ORIGIN_TAGS) as LotOrigin[]).find(
      (key) => LOT_ORIGIN_TAGS[key] === lot[2],
    );
    const date = decodeAnyDate(lot[3]!);
    return [
      { value: lot[1]!, label: 'item' },
      { value: lot[2]!, label: origin ? LOT_ORIGIN_LABELS[origin] : 'origin' },
      { value: lot[3]!, label: date ? `date ${isoDate(date)}` : 'date' },
      { value: lot[4]!, label: 'sequence' },
    ];
  }

  // RCV-<date>-<NNN>
  const recovery = /^RCV-([0-9]{2}[0-9A-Z]{2}|\d{8})-(\d{3,})$/.exec(value);
  if (recovery) {
    const date = decodeAnyDate(recovery[1]!);
    return [
      { value: 'RCV', label: 'recovery' },
      { value: recovery[1]!, label: date ? `date ${isoDate(date)}` : 'date' },
      { value: recovery[2]!, label: 'sequence' },
    ];
  }

  const [head = '', ...rest] = value.toUpperCase().split('-');

  const itemKind = ITEM_KINDS.find((kind) => ITEM_CODE_PREFIXES[kind] === head);
  if (itemKind) return masterDataSegments(head, ITEM_KIND_LABELS[itemKind], rest);

  if (head === 'SUP') return masterDataSegments(head, 'supplier', rest);
  if (head === 'RCP') return masterDataSegments(head, 'recipe', rest);

  const locationType = LOCATION_TYPES.find((type) => LOCATION_CODE_PREFIXES[type] === head);
  if (locationType) {
    return rest.length === 0
      ? [{ value: head, label: LOCATION_TYPE_LABELS[locationType] }]
      : [
          { value: head, label: LOCATION_TYPE_LABELS[locationType] },
          ...rest.map((part) => ({ value: part, label: 'label' })),
        ];
  }

  return [{ value, label: 'code' }];
}
